import dayjs from 'dayjs'

/**
 * Get the date format from localStorage settings
 */
export function getDateFormat(): string {
  try {
    const appSettings = localStorage.getItem('appSettings')
    if (appSettings) {
      const settings = JSON.parse(appSettings)
      return settings.dateFormat || 'DD/MM/YYYY'
    }
  } catch (error) {
    console.error('Failed to load date format:', error)
  }
  return 'DD/MM/YYYY' // Default format
}

/**
 * Format a date string or Date object according to user's preferred format
 */
export function formatDate(date: string | Date | undefined | null): string {
  if (!date) return '-'
  
  const format = getDateFormat()
  return dayjs(date).format(format)
}

/**
 * Format a date with time
 */
export function formatDateTime(date: string | Date | undefined | null): string {
  if (!date) return '-'
  
  const format = getDateFormat()
  return dayjs(date).format(`${format} HH:mm:ss`)
}

/**
 * Format a date with short time (without seconds)
 */
export function formatDateTimeShort(date: string | Date | undefined | null): string {
  if (!date) return '-'
  
  const format = getDateFormat()
  return dayjs(date).format(`${format} HH:mm`)
}

/**
 * Format time only
 */
export function formatTime(date: string | Date | undefined | null): string {
  if (!date) return '-'
  
  return dayjs(date).format('HH:mm:ss')
}

/**
 * Format time without seconds
 */
export function formatTimeShort(date: string | Date | undefined | null): string {
  if (!date) return '-'
  
  return dayjs(date).format('HH:mm')
}

/**
 * Format relative time (e.g., "2 hours ago", "just now")
 */
export function formatRelativeTime(date: string | Date | undefined | null): string {
  if (!date) return '-'
  
  const now = dayjs()
  const then = dayjs(date)
  const diffInMinutes = now.diff(then, 'minute')
  
  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  
  const diffInHours = now.diff(then, 'hour')
  if (diffInHours < 24) return `${diffInHours}h ago`
  
  const diffInDays = now.diff(then, 'day')
  if (diffInDays < 7) return `${diffInDays}d ago`
  
  // For older dates, show the formatted date
  return formatDate(date)
}

/**
 * Format date for display in short format (e.g., "Jan 15")
 */
export function formatDateShort(date: string | Date | undefined | null): string {
  if (!date) return '-'
  
  return dayjs(date).format('MMM DD')
}

/**
 * Format date with month name
 */
export function formatDateLong(date: string | Date | undefined | null): string {
  if (!date) return '-'
  
  return dayjs(date).format('MMMM DD, YYYY')
}
